import { SURPRISE_AND_DELIGHT_FREE_DELIVERY_TILE, isFreeDeliverySentinel } from '@nandosaus/constants';
import { useRootStore } from '@nandosaus/state-management';
import { useMemo } from 'react';

/**
 * Resolve each PLU in `eligibleGiftPlus` to a `{ plu, name, description, image }`
 * tile descriptor for the Surprise & Delight gift picker.
 *
 * The Free Delivery sentinel is special-cased BEFORE the menu lookup — it
 * isn't a real product so the menu store has nothing to resolve. Real PLUs
 * fall through to `MenuStore.menu.getProductByPlu`; PLUs not loaded yet
 * return a tile with `name = plu` and `image = null` so the picker still
 * renders something (the gift-option falls back to its placeholder when
 * image is null).
 */
const useEligibleGiftDetails = (eligibleGiftPlus = []) => {
  const { MenuStore } = useRootStore();
  const menu = MenuStore && MenuStore.menu;

  return useMemo(() => {
    if (!eligibleGiftPlus || eligibleGiftPlus.length === 0) {
      return {};
    }

    const result = {};
    eligibleGiftPlus.forEach(plu => {
      if (isFreeDeliverySentinel(plu)) {
        result[plu] = {
          plu,
          name: SURPRISE_AND_DELIGHT_FREE_DELIVERY_TILE.name,
          description: SURPRISE_AND_DELIGHT_FREE_DELIVERY_TILE.description,
          image: null,
        };
        return;
      }

      const product = menu && typeof menu.getProductByPlu === 'function' ? menu.getProductByPlu(plu) : null;
      if (product) {
        result[plu] = {
          plu,
          name: product.name || plu,
          description: product.description || null,
          image: product.image || null,
        };
      } else {
        // Not loaded yet — picker falls back to PLU + placeholder.
        result[plu] = { plu, name: plu, description: null, image: null };
      }
    });
    return result;
  }, [eligibleGiftPlus, menu]);
};

export { useEligibleGiftDetails };
